import type { TFunction } from 'i18next';

import i18n from '../../../../i18n/i18n';

/**
 * Copy for the Invite Friends screen, as the Notion spec "Referral / Invite Friends System" words
 * it. Kept in one module the way `friendsCatalog` keeps the friends tab's. Every string is resolved
 * through i18n when it is read (components pass the `t` from `useTranslation`, so a language change
 * re-renders them), never at module load - translations arrive from the backend after start-up.
 * Amounts and the milestone level come from the backend, not from here.
 */
export const getInviteCardCopy = (t: TFunction) => ({
	title: t('T_SITE_REFERRAL_TITLE', { defaultValue: 'Invite Friends' }),
	subtitle: t('T_SITE_REFERRAL_SUBTITLE', { defaultValue: 'Play together and earn rewards!' }),
});

export const getInviteScreenCopy = (t: TFunction) => ({
	title: t('T_SITE_REFERRAL_TITLE', { defaultValue: 'Invite Friends' }),
	heroTitle: t('T_SITE_REFERRAL_HERO_TITLE', { defaultValue: 'Invite Friends.\nEarn Rewards!' }),
	heroBody: (level: number, amount: string) =>
		t('T_SITE_REFERRAL_HERO_BODY', {
			defaultValue:
				"Share your invite link with friends. When they reach Level {{level}}, you'll receive {{amount}} chips!",
			level,
			amount,
		}),
	rewardBadge: t('T_SITE_REFERRAL_REWARD_BADGE', { defaultValue: 'REWARD PER FRIEND' }),
	rewardChips: (amount: string) =>
		t('T_SITE_REFERRAL_REWARD_CHIPS', { defaultValue: '{{amount}} Chips', amount }),
	linkTitle: t('T_SITE_REFERRAL_LINK_TITLE', { defaultValue: 'Your Invite Link' }),
	codeTitle: t('T_SITE_REFERRAL_CODE_TITLE', { defaultValue: 'Your Invite Code' }),
	linkCopied: t('T_SITE_REFERRAL_LINK_COPIED', { defaultValue: 'Link copied!' }),
	codeCopied: t('T_SITE_REFERRAL_CODE_COPIED', { defaultValue: 'Code copied!' }),
	share: t('T_SITE_REFERRAL_SHARE', { defaultValue: 'Share my referral link' }),
	howItWorks: t('T_SITE_REFERRAL_HOW_IT_WORKS', { defaultValue: 'How it works?' }),
	invitedPlayers: t('T_SITE_REFERRAL_INVITED_PLAYERS', { defaultValue: 'Invited Players' }),
	rewardsClaimed: t('T_SITE_REFERRAL_REWARDS_CLAIMED', { defaultValue: 'Rewards Claimed' }),
	listTitle: t('T_SITE_REFERRAL_INVITED_PLAYERS', { defaultValue: 'Invited Players' }),
	empty: t('T_SITE_REFERRAL_EMPTY', {
		defaultValue: 'No one has joined with your link yet. Share it to start earning!',
	}),
	disabled: t('T_SITE_REFERRAL_DISABLED', {
		defaultValue: 'Referral rewards are paused right now.',
	}),
	loadError: t('T_SITE_REFERRAL_LOAD_ERROR', { defaultValue: 'Could not load your invites.' }),
	// Mixed case on screen; the only existing keys (T_RB_TryAgain, T_SITE_MISSIONS_CLAIM/_CLAIMED)
	// are ALL CAPS, so these stay English until they get keys of their own.
	retry: 'Try again',
	claim: 'Claim',
	claimed: 'Claimed',
	claimSuccess: (amount: string) =>
		t('T_SITE_REFERRAL_CLAIM_SUCCESS', {
			defaultValue: 'Reward claimed! You received {{amount}} chips.',
			amount,
		}),
	level: (level: number, required: number) =>
		t('T_SITE_REFERRAL_LEVEL_PROGRESS', {
			defaultValue: 'Level {{level}}/{{required}}',
			level: Math.min(level, required),
			required,
		}),
	active: t('T_SITE_REFERRAL_ACTIVE', { defaultValue: 'Active' }),
});

/**
 * Shown by the native share sheet; the link (or the bare code) is appended. Called from plain
 * modules (`shareInvite`, `shareTargets`), so it reads the current language from `i18n` directly.
 */
export const shareMessage = (target: string) =>
	i18n.t('T_SITE_REFERRAL_SHARE_MESSAGE', {
		defaultValue: 'Join me on Svara!\nUse my invite link to get started:\n{{target}}',
		target,
	});

export const getHowItWorksCopy = (t: TFunction) => ({
	title: t('T_SITE_REFERRAL_HOW_IT_WORKS', { defaultValue: 'How It Works' }),
	steps: (level: number) => [
		{
			title: t('T_SITE_REFERRAL_STEP_1_TITLE', { defaultValue: 'Share your link' }),
			body: t('T_SITE_REFERRAL_STEP_1_BODY', {
				defaultValue: 'Send your personal referral link to friends.',
			}),
		},
		{
			title: t('T_SITE_REFERRAL_STEP_2_TITLE', { defaultValue: 'Your friend registers' }),
			body: t('T_SITE_REFERRAL_STEP_2_BODY', {
				defaultValue:
					'When they create a Svara account using your link, they are associated with your referral.',
			}),
		},
		{
			title: t('T_SITE_REFERRAL_STEP_3_TITLE', { defaultValue: 'They play and level up' }),
			body: t('T_SITE_REFERRAL_STEP_3_BODY', {
				defaultValue: 'Their progress is tracked automatically.',
			}),
		},
		{
			title: t('T_SITE_REFERRAL_STEP_4_TITLE', {
				defaultValue: 'They reach Level {{level}}',
				level,
			}),
			body: t('T_SITE_REFERRAL_STEP_4_BODY', {
				defaultValue: 'Once they reach Level {{level}}, your referral reward becomes available.',
				level,
			}),
		},
		{
			title: t('T_SITE_REFERRAL_STEP_5_TITLE', { defaultValue: 'Claim your reward' }),
			body: t('T_SITE_REFERRAL_STEP_5_BODY', {
				defaultValue: 'Return to Invite Friends and claim your reward.',
			}),
		},
	],
	close: t('T_SITE_REFERRAL_GOT_IT', { defaultValue: 'Got it' }),
});

/** The backend's stable error keys, mapped to the i18n key and English a player should see. */
const CLAIM_ERRORS: Record<string, [key: string, defaultValue: string]> = {
	svara_referral_already_claimed: [
		'T_SITE_REFERRAL_ERROR_ALREADY_CLAIMED',
		'This reward was already claimed.',
	],
	svara_referral_level_not_reached: [
		'T_SITE_REFERRAL_ERROR_LEVEL_NOT_REACHED',
		'Your friend has not reached the required level yet.',
	],
	svara_referral_disabled: ['T_SITE_REFERRAL_DISABLED', 'Referral rewards are paused right now.'],
	svara_referral_max_rewards_reached: [
		'T_SITE_REFERRAL_ERROR_MAX_REACHED',
		'You have reached the maximum number of referral rewards.',
	],
	svara_referral_player_not_active: [
		'T_SITE_REFERRAL_ERROR_NOT_ELIGIBLE',
		'This player is no longer eligible for a reward.',
	],
	svara_referral_claim_in_progress: [
		'T_SITE_REFERRAL_ERROR_PROCESSING',
		'Your claim is being processed. Please wait a moment.',
	],
};

export const claimErrorMessage = (t: TFunction, error: any) => {
	const message = String(error?.response?.data?.message ?? error?.message ?? '');
	const match = Object.keys(CLAIM_ERRORS).find(candidate => message.includes(candidate));
	if (match) {
		const [key, defaultValue] = CLAIM_ERRORS[match];
		return t(key, { defaultValue });
	}
	return t('T_SITE_MISSIONS_ERROR_FAILED', {
		defaultValue: 'Could not claim the reward. Please try again.',
	});
};

export const formatChips = (amount: number) => Math.round(amount).toLocaleString('en-US');

/** "Last seen 2d ago" - the invitee row's offline status line, compact enough for one line. */
export const lastSeenLabel = (t: TFunction, iso: string | null) => {
	if (!iso) return t('T_SITE_LAST_SEEN_NEVER', { defaultValue: 'Not seen yet' });

	const minutes = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
	const units: Array<[number, string, string]> = [
		[60 * 24 * 7, 'T_SITE_LAST_SEEN_WEEKS', 'Last seen {{count}}w ago'],
		[60 * 24, 'T_SITE_LAST_SEEN_DAYS', 'Last seen {{count}}d ago'],
		[60, 'T_SITE_LAST_SEEN_HOURS', 'Last seen {{count}}h ago'],
		[1, 'T_SITE_LAST_SEEN_MINUTES', 'Last seen {{count}}m ago'],
	];
	for (const [size, key, defaultValue] of units) {
		if (minutes >= size) {
			return t(key, { defaultValue, count: Math.floor(minutes / size) });
		}
	}
	return t('T_SITE_LAST_SEEN_JUST_NOW', { defaultValue: 'Last seen just now' });
};
